import React, { useState } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Button, 
  Typography, 
  Space, 
  Badge,
  List,
  Divider,
  Tag,
  Modal,
  Alert,
  message
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  CheckOutlined,
  CrownOutlined,
  StarOutlined,
  PayCircleOutlined,
  CreditCardOutlined,
  InfoCircleOutlined,
  FireOutlined,
  ThunderboltOutlined
} from '@ant-design/icons';
import styled from 'styled-components';
import { chatGPTTheme } from '../../assets/styles/theme';
import { loadStripe } from '@stripe/stripe-js';
import { PaymentAPI } from '../../shared/api/conversationService';
import { AuthService } from '../../shared/api/authService';

const { Title, Paragraph, Text } = Typography;

// 定价数据类型
interface PricingPlan {
  id: string;
  name: string;
  nameJa: string;
  price: number;
  originalPrice?: number;
  currency: string;
  period?: string;
  conversations: number | string;
  validity?: number;
  features: string[];
  popular?: boolean;
  recommended?: boolean;
  badge?: string;
  color: string;
  type: 'single' | 'monthly' | 'yearly';
  savings?: string;
}

// Styled Components
const PricingContainer = styled.div`
  padding: ${chatGPTTheme.spacing.xl};
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: ${chatGPTTheme.spacing.lg} ${chatGPTTheme.spacing.md}; /* 移动端减少内边距 */
  }
`;

const HeroSection = styled.div`
  text-align: center;
  margin-bottom: ${chatGPTTheme.spacing.xxl};
  
  @media (max-width: 768px) {
    margin-bottom: ${chatGPTTheme.spacing.lg}; /* 移动端减少下边距 */
    padding: 0 ${chatGPTTheme.spacing.md}; /* 添加左右内边距 */
  }
`;

const StyledTitle = styled(Title)`
  &.ant-typography {
    margin-bottom: ${chatGPTTheme.spacing.md};
    
    @media (max-width: 768px) {
      font-size: 24px !important; /* 移动端使用更小的标题字体 */
      margin-bottom: ${chatGPTTheme.spacing.sm}; /* 减少下边距 */
      line-height: 1.3; /* 调整行高 */
    }
  }
`;

const StyledSubtitle = styled(Paragraph)`
  &.ant-typography {
    font-size: 1.1rem;
    color: ${chatGPTTheme.colors.textSecondary};
    
    @media (max-width: 768px) {
      font-size: 0.95rem !important; /* 移动端使用更小的副标题字体 */
      line-height: 1.4; /* 调整行高 */
      margin-bottom: 0; /* 减少下边距 */
      padding: 0 ${chatGPTTheme.spacing.sm}; /* 添加左右内边距 */
    }
  }
`;

const PopularBadge = styled.div<{ $color: string }>`
  position: absolute;
  top: -10px;
  left: 50%;
  transform: translateX(-50%);
  background: ${props => props.$color};
  color: white;
  padding: ${chatGPTTheme.spacing.xs} ${chatGPTTheme.spacing.md};
  border-radius: ${chatGPTTheme.borderRadius.lg};
  font-size: 0.85rem;
  font-weight: 600;
  box-shadow: ${chatGPTTheme.shadows.md};
  z-index: 1;
`;

const PriceSection = styled.div`
  text-align: center;
  margin: ${chatGPTTheme.spacing.lg} 0;
`;

const PriceAmount = styled.div<{ $color: string }>`
  font-size: 2.5rem;
  font-weight: 700;
  color: ${props => props.$color};
  line-height: 1;
  
  .currency {
    font-size: 1.2rem;
    vertical-align: top;
  }
  
  .period {
    font-size: 1rem;
    color: ${chatGPTTheme.colors.textSecondary};
    font-weight: 400;
  }
`;

const OriginalPrice = styled.span`
  font-size: 1.2rem;
  color: ${chatGPTTheme.colors.textMuted};
  text-decoration: line-through;
  margin-right: ${chatGPTTheme.spacing.sm};
`;

const FeatureList = styled(List)`
  .ant-list-item {
    border: none;
    padding: ${chatGPTTheme.spacing.xs} 0;
    
    .anticon {
      color: ${chatGPTTheme.colors.success};
      margin-right: ${chatGPTTheme.spacing.sm};
    }
  }
`;

const ActionButton = styled(Button)<{ $color: string }>`
  &.ant-btn {
    width: 100%;
    height: 50px;
    border-radius: ${chatGPTTheme.borderRadius.lg};
    font-size: 1.1rem;
    font-weight: 600;
    background: ${props => props.$color};
    border-color: ${props => props.$color};
    
    &:hover {
      background: ${props => props.$color}dd;
      border-color: ${props => props.$color}dd;
      transform: translateY(-2px);
    }
  }
`;

const PricingCard = styled(Card)<{ $color: string; $isPopular?: boolean }>`
  &.ant-card {
    border-radius: ${chatGPTTheme.borderRadius.lg};
    transition: all 0.3s ease;
    cursor: pointer;
    position: relative;
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    
    &:hover {
      border-color: ${chatGPTTheme.colors.primary};
      box-shadow: ${chatGPTTheme.shadows.md};
    }
    
    &.selected {
      border-color: ${chatGPTTheme.colors.primary};
      background: ${chatGPTTheme.colors.primary}08;
    }
    
    .ant-card-body {
      padding: ${chatGPTTheme.spacing.md};
      text-align: center;
      flex: 1;
      display: flex;
      flex-direction: column;
      width: 100%;
    }
  }
`;

const PricingPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const { state } = useAuth();
  
  const [loading, setLoading] = useState(false);

  // 定价计划数组
  const pricingPlans: PricingPlan[] = React.useMemo(() => [
    // 单次分析计划
    {
      id: sessionId || 'single-analysis',
      name: 'Single Analysis',
      nameJa: t('pricing.plans.single.name'),
      price: 2980,
      currency: '¥',
      conversations: 1,
      features: t('pricing.plans.single.features', { returnObjects: true }) as string[],
      recommended: false,
      badge: t('pricing.plans.single.badge'),
      color: chatGPTTheme.colors.primary,
      type: 'single'
    },
    // 月度订阅计划
    {
      id: 'monthly-subscription',
      name: 'Monthly Plan',
      nameJa: t('pricing.plans.monthly.name'),
      price: 5980,
      currency: '¥',
      period: t('pricing.plans.monthly.period'),
      conversations: 3,
      validity: 30,
      features: t('pricing.plans.monthly.features', { returnObjects: true }) as string[],
      popular: true,
      recommended: true,
      badge: t('pricing.plans.monthly.badge'),
      color: chatGPTTheme.colors.success,
      type: 'monthly'
    },
    // 年度订阅计划
    {
      id: 'yearly-subscription',
      name: 'Yearly Plan',
      nameJa: t('pricing.plans.yearly.name'),
      price: 59800,
      originalPrice: 71760, // 12个月的原价
      currency: '¥',
      period: t('pricing.plans.yearly.period'),
      conversations: 36,
      validity: 365,
      features: t('pricing.plans.yearly.features', { returnObjects: true }) as string[],
      recommended: false,
      badge: t('pricing.plans.yearly.badge'),
      color: chatGPTTheme.colors.warning,
      type: 'yearly',
      savings: t('pricing.plans.yearly.savings')
    }
  ], [t, sessionId]);

  const handleSelectPlan = async (plan: PricingPlan) => {
    // 检查用户是否已登录
    if (!state.isAuthenticated) {
      Modal.confirm({
        title: t('pricing.login.required.title'),
        content: t('pricing.login.required.content'),
        okText: t('common.login'),
        cancelText: t('common.cancel'),
        onOk: () => navigate('/auth/login')
      });
      return;
    }
    
    // 直接处理支付，跳过选择支付方式的步骤
    await handlePayment(plan);
  };

  const handlePayment = async (plan: PricingPlan) => {
    try {
      setLoading(true);
      
      // 检查用户是否已登录且 token 有效
      if (!state.isAuthenticated || !AuthService.isTokenValid()) {
        message.error('请重新登录');
        navigate('/auth/login');
        return;
      }
      
      // 创建支付 - 直接使用信用卡支付
      const paymentData = {
        payment_method: 'card', // 直接使用信用卡支付
        analysis_type: plan.type === 'single' ? 'standard' : plan.type,
        session_id: plan.type === 'single' ? sessionId : undefined,
        amount_jpy: plan.price,
        plan_type: plan.type,
        plan_id: plan.id
      };
      
      const response = await PaymentAPI.createPayment(paymentData);
      
      if (response.payment_id && response.payment_url) {
        // 直接重定向到 Stripe Checkout
        window.location.href = response.payment_url;
      } else {
        message.error(t('payment.error.createFailed'));
      }
    } catch (error) {
      console.error('支付创建失败:', error);
      if (error instanceof Error && error.message.includes('无效的访问令牌')) {
        message.error('登录已过期，请重新登录');
        navigate('/auth/login');
      } else {
        message.error(t('payment.error.createFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const renderPricingCard = (plan: PricingPlan) => (
    <Col xs={24} sm={24} md={8} key={plan.id} style={{ display: 'flex', flex: '1 1 0px' }}>
      <PricingCard $color={plan.color} $isPopular={plan.popular}>
        {(plan.popular || plan.recommended) && (
          <PopularBadge $color={plan.color}>
            {plan.popular && <FireOutlined />}
            {plan.recommended && <CrownOutlined />}
            {!plan.popular && !plan.recommended && <StarOutlined />}
            {' '}
            {plan.badge}
          </PopularBadge>
        )}
        
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <div style={{ textAlign: 'center', marginTop: chatGPTTheme.spacing.xl, marginBottom: chatGPTTheme.spacing.lg }}>
            <Title level={3} style={{ margin: 0, color: plan.color }}>
              {plan.nameJa}
            </Title>
            {plan.savings && (
              <Tag color="red" style={{ marginTop: chatGPTTheme.spacing.xs }}>
                <ThunderboltOutlined /> {plan.savings}
              </Tag>
            )}
          </div>

          <PriceSection>
            <PriceAmount $color={plan.color}>
              {plan.originalPrice && (
                <OriginalPrice>{plan.currency}{plan.originalPrice.toLocaleString()}</OriginalPrice>
              )}
              <span className="currency">{plan.currency}</span>
              {plan.price.toLocaleString()}
              {plan.period && <span className="period">{plan.period}</span>}
            </PriceAmount>
            
            <div style={{ marginTop: chatGPTTheme.spacing.sm }}>
              <Tag color={plan.color} style={{ fontSize: '0.9rem', padding: '4px 12px' }}>
                {typeof plan.conversations === 'number' 
                  ? `${plan.conversations}回相談` 
                  : plan.conversations}
              </Tag>
              {plan.validity && (
                <Tag style={{ marginLeft: chatGPTTheme.spacing.xs }}>
                  {plan.validity}日間有効
                </Tag>
              )}
            </div>
          </PriceSection>

          <div style={{ flex: 1 }}>
            <FeatureList
              size="small"
              dataSource={plan.features}
              renderItem={(feature) => (
                <List.Item key={String(feature)}>
                  <CheckOutlined style={{ marginRight: 8 }} />
                  {String(feature)}
                </List.Item>
              )}
            />
          </div>

          <div style={{ marginTop: chatGPTTheme.spacing.lg }}>
            <ActionButton
              type="primary"
              $color={plan.color}
              loading={loading}
              onClick={() => {
                if (!state.isAuthenticated) {
                  navigate(`/login?returnUrl=${encodeURIComponent(window.location.pathname + window.location.search)}`);
                } else {
                  handleSelectPlan(plan);
                }
              }}
            >
              {plan.type === 'single' && <StarOutlined />}
              {plan.type === 'monthly' && <FireOutlined />}
              {plan.type === 'yearly' && <CrownOutlined />}
              {state.isAuthenticated 
                ? t('pricing.startNow')
                : t('pricing.aiCaseAnalysis.loginToAnalyze')
              }
            </ActionButton>
          </div>
        </div>
      </PricingCard>
    </Col>
  );

  return (
    <PricingContainer>
      <HeroSection>
        <StyledTitle level={1}>
          {t('pricing.title')}
        </StyledTitle>
        <StyledSubtitle>
          {t('pricing.subtitle')}
        </StyledSubtitle>
      </HeroSection>

      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <Row justify="center" gutter={[24, 24]} style={{ display: 'flex', alignItems: 'stretch', width: '100%' }}>
          {pricingPlans.map(plan => renderPricingCard(plan))}
        </Row>
      </div>

      {/* 未登录用户提示移到页面底部 */}
      {!state.isAuthenticated && (
        <Alert
          message={t('pricing.login.required.alert')}
          type="info"
          showIcon
          style={{ margin: '32px auto', maxWidth: 800 }}
        />
      )}
    </PricingContainer>
  );
};

export default PricingPage;
